import { AIR } from '../constants';
import { Rng } from '../math';
import { BinaryReader, BinaryWriter } from '../net/codec';
import { MatFlag } from './materials';
import type { VoxelWorld } from './world';

/**
 * Voxel operations are the only way the world changes during a match. The server decides
 * them and broadcasts them in tick order; every client applies the identical integer math,
 * so worlds never need to be streamed after the initial join.
 */
export const Op = {
  /** Explicit list of (x, y, z, power) damage entries. */
  Damage: 1,
  /** Spherical damage with falloff. Radius is in 1/16 voxel units. */
  Sphere: 2,
  /** Explicit list of (x, y, z, value) placements into air. */
  Place: 3,
  /** Fill air cells in a half-open box with a value. */
  PlaceBox: 4,
  /** Remove every voxel in a mask (small islands crumbling into debris). */
  Crumble: 5,
  /** Remove an island and remember it as a falling body. */
  FallStart: 6,
  /** A falling body landed `dy` voxels lower; some voxels shatter, the rest are re-inserted. */
  FallLand: 7,
} as const;
export type OpType = (typeof Op)[keyof typeof Op];

export type VoxelOp =
  | { t: typeof Op.Damage; list: number[] }
  | { t: typeof Op.Sphere; x: number; y: number; z: number; r16: number; power: number }
  | { t: typeof Op.Place; list: number[] }
  | { t: typeof Op.PlaceBox; x0: number; y0: number; z0: number; x1: number; y1: number; z1: number; v: number }
  | { t: typeof Op.Crumble; mask: VoxelMask }
  | { t: typeof Op.FallStart; id: number; mask: VoxelMask }
  | { t: typeof Op.FallLand; id: number; dy: number; seed: number; shatter: number };

/** Bitset over an axis-aligned box of voxels. Canonical order: x fastest, then z, then y. */
export class VoxelMask {
  readonly bits: Uint8Array;
  count = 0;

  constructor(
    readonly x0: number,
    readonly y0: number,
    readonly z0: number,
    readonly w: number,
    readonly h: number,
    readonly d: number,
    bits?: Uint8Array,
  ) {
    this.bits = bits ?? new Uint8Array(Math.ceil((w * h * d) / 8));
    if (bits) {
      let n = 0;
      for (let i = 0; i < bits.length; i++) {
        let b = bits[i];
        while (b) {
          n += b & 1;
          b >>= 1;
        }
      }
      this.count = n;
    }
  }

  static fromPacked(coords: ArrayLike<number>, n: number): VoxelMask {
    let minX = Infinity;
    let minY = Infinity;
    let minZ = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let maxZ = -Infinity;
    for (let i = 0; i < n * 3; i += 3) {
      const x = coords[i];
      const y = coords[i + 1];
      const z = coords[i + 2];
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (z < minZ) minZ = z;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
      if (z > maxZ) maxZ = z;
    }
    const m = new VoxelMask(minX, minY, minZ, maxX - minX + 1, maxY - minY + 1, maxZ - minZ + 1);
    for (let i = 0; i < n * 3; i += 3) m.add(coords[i], coords[i + 1], coords[i + 2]);
    return m;
  }

  private idx(x: number, y: number, z: number): number {
    return x - this.x0 + this.w * (z - this.z0 + this.d * (y - this.y0));
  }

  add(x: number, y: number, z: number): void {
    const i = this.idx(x, y, z);
    const byte = i >> 3;
    const bit = 1 << (i & 7);
    if ((this.bits[byte] & bit) === 0) {
      this.bits[byte] |= bit;
      this.count++;
    }
  }

  has(x: number, y: number, z: number): boolean {
    if (x < this.x0 || y < this.y0 || z < this.z0 || x >= this.x0 + this.w || y >= this.y0 + this.h || z >= this.z0 + this.d) {
      return false;
    }
    const i = this.idx(x, y, z);
    return (this.bits[i >> 3] & (1 << (i & 7))) !== 0;
  }

  /** Visits set voxels in canonical order. */
  forEach(fn: (x: number, y: number, z: number) => void): void {
    const { w, h, d, x0, y0, z0, bits } = this;
    let i = 0;
    for (let y = 0; y < h; y++) {
      for (let z = 0; z < d; z++) {
        for (let x = 0; x < w; x++, i++) {
          if ((bits[i >> 3] & (1 << (i & 7))) !== 0) fn(x0 + x, y0 + y, z0 + z);
        }
      }
    }
  }

  write(wr: BinaryWriter): void {
    wr.u16(this.x0).u16(this.y0).u16(this.z0).u16(this.w).u16(this.h).u16(this.d);
    // Run-length encode alternating runs of clear/set bits, starting with clear.
    const total = this.w * this.h * this.d;
    const runs: number[] = [];
    let cur = 0;
    let len = 0;
    for (let i = 0; i < total; i++) {
      const b = (this.bits[i >> 3] >> (i & 7)) & 1;
      if (b === cur) {
        len++;
      } else {
        runs.push(len);
        cur = b;
        len = 1;
      }
    }
    runs.push(len);
    wr.varint(runs.length);
    for (const r of runs) wr.varint(r);
  }

  static read(r: BinaryReader): VoxelMask {
    const x0 = r.u16();
    const y0 = r.u16();
    const z0 = r.u16();
    const w = r.u16();
    const h = r.u16();
    const d = r.u16();
    const m = new VoxelMask(x0, y0, z0, w, h, d);
    const nRuns = r.varint();
    let i = 0;
    let cur = 0;
    for (let k = 0; k < nRuns; k++) {
      const len = r.varint();
      if (cur === 1) {
        for (let j = i; j < i + len; j++) m.bits[j >> 3] |= 1 << (j & 7);
        m.count += len;
      }
      i += len;
      cur ^= 1;
    }
    return m;
  }
}

/** Records voxel changes produced by applying ops: flat quadruples (x, y, z, value). */
export class ChangeLog {
  removed: number[] = [];
  placed: number[] = [];
  clear(): void {
    this.removed.length = 0;
    this.placed.length = 0;
  }
}

export interface FallingIsland {
  id: number;
  mask: VoxelMask;
  /** Palette values in mask canonical order. */
  values: Uint8Array;
}

/** Holds islands between FallStart and FallLand. Server and clients each keep one. */
export class FallingStore {
  readonly islands = new Map<number, FallingIsland>();
}

/**
 * Terrain only craters within this fraction of a sphere's radius (squared, in percent: 0.4² = 16%),
 * so explosions pock the ground instead of digging pits while structures take the full blast.
 */
export const TERRAIN_BLAST_R2_PCT = 16;

/** Damage multiplier curve for sphere ops: full power at the center, 35% at the rim. */
export function sphereDamage(power: number, d2: number, r2: number): number {
  return Math.floor((power * (100 * r2 - 65 * d2)) / (100 * r2));
}

export function applyOp(world: VoxelWorld, op: VoxelOp, log: ChangeLog | null, falling: FallingStore): void {
  switch (op.t) {
    case Op.Damage: {
      const l = op.list;
      for (let i = 0; i < l.length; i += 4) {
        const v = world.damage(l[i], l[i + 1], l[i + 2], l[i + 3]);
        if (v !== AIR && log) log.removed.push(l[i], l[i + 1], l[i + 2], v);
      }
      return;
    }
    case Op.Sphere: {
      const r2 = op.r16 * op.r16;
      const R = Math.ceil(op.r16 / 16);
      for (let dy = -R; dy <= R; dy++) {
        for (let dz = -R; dz <= R; dz++) {
          for (let dx = -R; dx <= R; dx++) {
            const d2 = (dx * dx + dy * dy + dz * dz) * 256;
            if (d2 > r2) continue;
            const dmg = sphereDamage(op.power, d2, r2);
            if (dmg <= 0) continue;
            const x = op.x + dx;
            const y = op.y + dy;
            const z = op.z + dz;
            if (d2 * 100 > r2 * TERRAIN_BLAST_R2_PCT && (world.mats.flags[world.get(x, y, z)] & MatFlag.Terrain) !== 0) continue;
            const v = world.damage(x, y, z, dmg);
            if (v !== AIR && log) log.removed.push(x, y, z, v);
          }
        }
      }
      return;
    }
    case Op.Place: {
      const l = op.list;
      for (let i = 0; i < l.length; i += 4) {
        const x = l[i];
        const y = l[i + 1];
        const z = l[i + 2];
        if (!world.inBounds(x, y, z) || world.get(x, y, z) !== AIR) continue;
        world.set(x, y, z, l[i + 3]);
        if (log) log.placed.push(x, y, z, l[i + 3]);
      }
      return;
    }
    case Op.PlaceBox: {
      for (let y = op.y0; y < op.y1; y++) {
        for (let z = op.z0; z < op.z1; z++) {
          for (let x = op.x0; x < op.x1; x++) {
            if (!world.inBounds(x, y, z) || world.get(x, y, z) !== AIR) continue;
            world.set(x, y, z, op.v);
            if (log) log.placed.push(x, y, z, op.v);
          }
        }
      }
      return;
    }
    case Op.Crumble: {
      op.mask.forEach((x, y, z) => {
        const v = world.set(x, y, z, AIR);
        if (v !== AIR && log) log.removed.push(x, y, z, v);
      });
      return;
    }
    case Op.FallStart: {
      const values = new Uint8Array(op.mask.count);
      let i = 0;
      op.mask.forEach((x, y, z) => {
        values[i++] = world.set(x, y, z, AIR);
      });
      falling.islands.set(op.id, { id: op.id, mask: op.mask, values });
      return;
    }
    case Op.FallLand: {
      const isl = falling.islands.get(op.id);
      if (!isl) return;
      falling.islands.delete(op.id);
      const rng = new Rng(op.seed);
      let i = 0;
      isl.mask.forEach((x, y, z) => {
        const v = isl.values[i++];
        const shattered = rng.next() * 100 < op.shatter;
        if (v === AIR) return;
        const ty = y - op.dy;
        if (shattered || !world.inBounds(x, ty, z) || world.get(x, ty, z) !== AIR) {
          if (log) log.removed.push(x, ty, z, v);
          return;
        }
        world.set(x, ty, z, v);
        if (log) log.placed.push(x, ty, z, v);
      });
      return;
    }
  }
}

const isU16 = (v: number): boolean => Number.isInteger(v) && v >= 0 && v <= 0xffff;

/**
 * True when writeOp sends the op's coordinates exactly. An out-of-range value would wrap on the wire
 * (a box ending at x = -11 arrives as x = 65525 and fills the map on every client), so the server
 * must not apply such an op.
 */
export function opFitsWire(op: VoxelOp): boolean {
  switch (op.t) {
    case Op.Sphere:
      return isU16(op.x) && isU16(op.y) && isU16(op.z) && isU16(op.r16);
    case Op.PlaceBox:
      return isU16(op.x0) && isU16(op.y0) && isU16(op.z0) && isU16(op.x1) && isU16(op.y1) && isU16(op.z1);
    default:
      return true;
  }
}

export function writeOp(w: BinaryWriter, op: VoxelOp): void {
  w.u8(op.t);
  switch (op.t) {
    case Op.Damage:
    case Op.Place: {
      const n = op.list.length / 4;
      w.varint(n);
      for (let i = 0; i < op.list.length; i += 4) {
        w.u16(op.list[i]).u16(op.list[i + 1]).u16(op.list[i + 2]).u8(op.list[i + 3]);
      }
      return;
    }
    case Op.Sphere:
      w.u16(op.x).u16(op.y).u16(op.z).u16(op.r16).u8(op.power);
      return;
    case Op.PlaceBox:
      w.u16(op.x0).u16(op.y0).u16(op.z0).u16(op.x1).u16(op.y1).u16(op.z1).u8(op.v);
      return;
    case Op.Crumble:
      op.mask.write(w);
      return;
    case Op.FallStart:
      w.varint(op.id);
      op.mask.write(w);
      return;
    case Op.FallLand:
      w.varint(op.id).u16(op.dy).u32(op.seed).u8(op.shatter);
      return;
  }
}

export function readOp(r: BinaryReader): VoxelOp {
  const t = r.u8();
  switch (t) {
    case Op.Damage:
    case Op.Place: {
      const n = r.varint();
      const list = new Array<number>(n * 4);
      for (let i = 0; i < n * 4; i += 4) {
        list[i] = r.u16();
        list[i + 1] = r.u16();
        list[i + 2] = r.u16();
        list[i + 3] = r.u8();
      }
      return { t, list } as VoxelOp;
    }
    case Op.Sphere:
      return { t, x: r.u16(), y: r.u16(), z: r.u16(), r16: r.u16(), power: r.u8() };
    case Op.PlaceBox:
      return { t, x0: r.u16(), y0: r.u16(), z0: r.u16(), x1: r.u16(), y1: r.u16(), z1: r.u16(), v: r.u8() };
    case Op.Crumble:
      return { t, mask: VoxelMask.read(r) };
    case Op.FallStart:
      return { t, id: r.varint(), mask: VoxelMask.read(r) };
    case Op.FallLand:
      return { t, id: r.varint(), dy: r.u16(), seed: r.u32(), shatter: r.u8() };
    default:
      throw new Error(`Unknown voxel op ${t}`);
  }
}
